import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchInfo } from "../lib/hyperliquid";
import { subscribe } from "../lib/hyperliquid";
import { formatPrice, formatNumber } from "../lib/format";
import LiveOnChainFeed from "../components/LiveOnChainFeed";

export default function Landing() {
    const [mids, setMids] = useState({});
    const [meta, setMeta] = useState(null);

    useEffect(() => {
        fetchInfo({ type: "meta" }).then(setMeta).catch(() => {});
        const unsub = subscribe({ type: "allMids" }, (d) => setMids(d.mids || {}));
        return unsub;
    }, []);

    const headlinePairs = ["BTC", "ETH", "SOL", "HYPE"];
    // Cross pairs showcased on the landing page — popular bases that all have
    // a live Hyperliquid USD oracle, so the synthetic ratio is always
    // computable.
    const crossPairs = [
        ["BTC", "ETH"],
        ["BTC", "HYPE"],
        ["ETH", "SOL"],
        ["ETH", "HYPE"],
        ["SOL", "HYPE"],
        ["BTC", "SOL"],
    ];

    return (
        <div className="hero-bg min-h-screen">
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-[#1F1F24]">
                <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
                <div className="absolute inset-0 bg-noise opacity-60 pointer-events-none" />
                <div className="max-w-[1480px] mx-auto px-5 sm:px-6 lg:px-12 pt-14 pb-20 sm:pt-24 sm:pb-32 relative">
                    <div className="inline-flex items-center gap-2 mb-6 sm:mb-8 border border-[#1F1F24] bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 pulse-dot" />
                        <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-400">Live Hyperliquid Oracle / Testnet v1.0</span>
                    </div>

                    <h1 className="text-4xl sm:text-5xl lg:text-7xl font-medium tracking-tighter leading-[1.02] sm:leading-[0.95] max-w-5xl">
                        Bilateral derivative swaps with <span className="text-cyan-400">bounded exposure.</span>
                    </h1>
                    <p className="mt-6 sm:mt-8 text-zinc-400 text-base sm:text-lg max-w-2xl leading-relaxed">
                        Verslan <span className="font-mono text-zinc-200">BUBE</span> — Bullish or Bearish Execution — pre-funded,
                        cash-settled directional swaps on Hyperliquid. No funding rates, no leverage cascades, risk capped at margin posted.
                    </p>

                    <div className="mt-8 sm:mt-12 flex flex-wrap items-center gap-3">
                        <Link
                            to="/auth?mode=register"
                            data-testid="hero-launch-btn"
                            className="px-6 py-3 bg-cyan-400 text-black font-semibold tracking-tight text-sm rounded-sm hover:bg-cyan-300 transition-colors"
                        >
                            Launch Testnet →
                        </Link>
                        <Link
                            to="/auth"
                            className="px-6 py-3 border border-[#1F1F24] hover:border-zinc-700 text-zinc-200 font-medium tracking-tight text-sm rounded-sm transition-colors"
                        >
                            Sign in
                        </Link>
                        <div
                            data-testid="mainnet-eta-badge"
                            className="inline-flex items-center gap-2.5 px-5 py-3 bg-gradient-to-r from-violet-500/20 via-fuchsia-500/15 to-violet-500/5 border-2 border-violet-400/80 rounded-sm shadow-lg shadow-violet-500/30"
                        >
                            <span className="relative flex w-2.5 h-2.5">
                                <span className="absolute inset-0 rounded-full bg-violet-400 animate-ping opacity-70" />
                                <span className="relative w-2.5 h-2.5 rounded-full bg-violet-400" />
                            </span>
                            <span className="text-sm sm:text-base font-mono uppercase tracking-[0.18em] font-bold text-violet-200 drop-shadow-[0_0_10px_rgba(167,139,250,0.55)]">
                                Mainnet · Q3–Q4 2026-27
                            </span>
                        </div>
                    </div>

                    {/* Live ticker strip — spot + cross pairs */}
                    <div className="mt-12 sm:mt-20 max-w-5xl">
                        <div className="flex items-center gap-2 mb-3">
                            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 pulse-dot" />
                            <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-400">USD spot · live Hyperliquid oracle</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {headlinePairs.map((coin) => {
                                const p = parseFloat(mids[coin] || 0);
                                return (
                                    <div key={coin} data-testid={`ticker-${coin}`} className="bg-[#0A0A0C]/80 backdrop-blur border border-[#1F1F24] p-4 rounded-sm">
                                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">{coin}-USD</div>
                                        <div className="font-mono text-2xl text-zinc-50 mt-2 tracking-tight">{p ? formatPrice(p) : "—"}</div>
                                        <div className="text-[10px] font-mono text-zinc-500 mt-1">Hyperliquid mid</div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="flex items-center gap-2 mt-6 mb-3">
                            <span className="w-1.5 h-1.5 rounded-full bg-violet-400 pulse-dot" />
                            <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-violet-300">Cross pairs · BUBE multi swaps</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                            {crossPairs.map(([a, b]) => {
                                const pa = parseFloat(mids[a] || 0);
                                const pb = parseFloat(mids[b] || 0);
                                const r = pa && pb ? pa / pb : null;
                                const formatted = r == null ? "—" : r >= 1000 ? formatPrice(r) : r >= 1 ? r.toFixed(4) : r.toFixed(6);
                                return (
                                    <div
                                        key={`${a}-${b}`}
                                        data-testid={`ticker-cross-${a}-${b}`}
                                        className="bg-gradient-to-br from-violet-500/[0.04] via-[#0A0A0C]/80 to-[#0A0A0C]/80 backdrop-blur border border-violet-500/20 p-3 rounded-sm"
                                    >
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-violet-300">{a}-{b}</span>
                                            <span className="text-[7.5px] font-mono uppercase tracking-[0.18em] text-violet-400/80 border border-violet-500/30 bg-violet-500/10 px-1 py-0.5 rounded-sm">synthetic</span>
                                        </div>
                                        <div className="font-mono text-lg text-zinc-50 mt-1.5 tracking-tight tabular-nums">{formatted}</div>
                                        <div className="text-[9px] font-mono text-zinc-500 mt-0.5">
                                            {pa && pb ? `${a}/USD ÷ ${b}/USD` : "loading…"}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Live on-chain feed — proves every trade hits Hyperliquid testnet */}
                    <div className="mt-6 sm:mt-8 max-w-4xl">
                        <LiveOnChainFeed limit={5} />
                    </div>
                </div>
            </section>

            {/* VALUE PROPS */}
            <section className="border-b border-[#1F1F24] py-14 sm:py-24 px-5 sm:px-6 lg:px-12">
                <div className="max-w-[1480px] mx-auto">
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400 mb-6">Protocol Design</div>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tighter max-w-3xl">
                        Pre-funded. Time-bound. Deterministic.
                    </h2>
                    <p className="mt-6 text-zinc-400 max-w-2xl text-base sm:text-lg leading-relaxed">
                        Every swap is a sealed bilateral contract between a bull and a bear, written against a live Hyperliquid oracle reference price.
                    </p>

                    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 mt-10 sm:mt-16">
                        {[
                            { kicker: "01", t: "2% minimum margin", b: "Default 50x leverage with deterministic margin framework. Margin requirement adjustable by protocol policy." },
                            { kicker: "02", t: "Tenors from 15m to 1yr", b: "Eight standard durations: 15m, 30m, 1h, 12h, 24h, 15 days, 1 month, 1 year. Cash-settled at expiry." },
                            { kicker: "03", t: "PnL capped at margin", b: "Loser's payout caps the winner's PnL. Each swap is self-contained — no rehypothecation, no cascading liquidations." },
                            { kicker: "04", t: "Long-vs-Short or Cross-Asset", b: "Trade directional views on any Hyperliquid asset, or against another asset (BTC/ETH ratio, etc.)." },
                            { kicker: "05", t: "Hyperliquid oracle", b: "Entry and exit prices read from Hyperliquid's mainnet WebSocket allMids feed for 230+ markets." },
                            { kicker: "06", t: "Auto-matched liquidity", b: "Counterparty bots provide instant matches on testnet so you can trade solo without waiting." },
                        ].map((f) => (
                            <div key={f.kicker} className="bg-[#0A0A0C] border border-[#1F1F24] p-6 sm:p-8 rounded-sm hover:border-zinc-700 transition-colors">
                                <div className="font-mono text-[10px] tracking-[0.22em] text-cyan-400 uppercase">{f.kicker}</div>
                                <h3 className="mt-4 text-lg sm:text-xl font-medium tracking-tight">{f.t}</h3>
                                <p className="mt-4 text-sm text-zinc-400 leading-relaxed">{f.b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* MARKETS COUNT */}
            <section className="border-b border-[#1F1F24] py-12 sm:py-16 px-5 sm:px-6 lg:px-12">
                <div className="max-w-[1480px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div className="bg-[#0A0A0C] border border-[#1F1F24] p-5 sm:p-8 rounded-sm">
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Supported markets</div>
                        <div className="mt-3 font-mono text-3xl sm:text-4xl text-zinc-50 tracking-tighter">{meta?.universe?.length ?? "—"}</div>
                        <div className="text-xs text-zinc-500 mt-2">Hyperliquid perp universe</div>
                    </div>
                    <div className="bg-[#0A0A0C] border border-[#1F1F24] p-5 sm:p-8 rounded-sm">
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Default leverage</div>
                        <div className="mt-3 font-mono text-3xl sm:text-4xl text-zinc-50 tracking-tighter">50<span className="text-cyan-400">×</span></div>
                        <div className="text-xs text-zinc-500 mt-2">Margin = 2% of notional</div>
                    </div>
                    <div className="bg-[#0A0A0C] border border-[#1F1F24] p-5 sm:p-8 rounded-sm">
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Settlement fee</div>
                        <div className="mt-3 font-mono text-3xl sm:text-4xl text-zinc-50 tracking-tighter">0.10<span className="text-zinc-600">%</span></div>
                        <div className="text-xs text-zinc-500 mt-2">10 bps on notional</div>
                    </div>
                    <div className="bg-[#0A0A0C] border border-[#1F1F24] p-5 sm:p-8 rounded-sm">
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Mid prices streaming</div>
                        <div className="mt-3 font-mono text-3xl sm:text-4xl text-zinc-50 tracking-tighter">{Object.keys(mids).length || "—"}</div>
                        <div className="text-xs text-zinc-500 mt-2">live WebSocket subscriptions</div>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="border-b border-[#1F1F24] py-14 sm:py-24 px-5 sm:px-6 lg:px-12">
                <div className="max-w-[1480px] mx-auto">
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400 mb-6">Flow</div>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tighter">From quote to cash settlement.</h2>
                    <div className="mt-10 sm:mt-16 grid sm:grid-cols-2 md:grid-cols-4 gap-2">
                        {[
                            ["Deposit", "Receive 1,000,000 mock USDC and a generated wallet address on registration."],
                            ["Quote", "Pick a market, side, notional, margin and tenor. Counterparty bot fills instantly."],
                            ["Hold", "Add variable margin if needed. Live mark-to-market against Hyperliquid oracle."],
                            ["Settle", "At expiry the protocol reads the exit oracle price and pays out the capped PnL."],
                        ].map(([t, b], i) => (
                            <div key={t} className="bg-[#0A0A0C] border border-[#1F1F24] p-6 rounded-sm">
                                <div className="font-mono text-[10px] tracking-[0.22em] text-zinc-500 uppercase">Step {String(i + 1).padStart(2, "0")}</div>
                                <h3 className="mt-3 text-lg font-medium">{t}</h3>
                                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 sm:py-24 px-5 sm:px-6 lg:px-12">
                <div className="max-w-[1480px] mx-auto text-center">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tighter">Open the terminal.</h2>
                    <p className="mt-6 text-zinc-400 max-w-xl mx-auto">Create an account, get faucet USDC, and start trading bounded-exposure swaps in seconds.</p>
                    <Link
                        to="/auth?mode=register"
                        data-testid="cta-launch-btn"
                        className="inline-block mt-10 px-8 py-3 bg-cyan-400 text-black font-semibold tracking-tight rounded-sm hover:bg-cyan-300 transition-colors"
                    >
                        Launch Testnet →
                    </Link>
                </div>
            </section>

            <footer className="border-t border-[#1F1F24] py-8 px-5 sm:px-6 lg:px-12">
                <div className="max-w-[1480px] mx-auto flex flex-col md:flex-row justify-between gap-4 text-xs text-zinc-500 font-mono">
                    <div>VERSLAN · BUBE Testnet · v1.0.0 · Built on Hyperliquid</div>
                    <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                        <a href="mailto:contact@verslan.xyz" className="hover:text-cyan-400">contact@verslan.xyz</a>
                        <span className="text-zinc-700">·</span>
                        <Link to="/how-it-works" className="hover:text-cyan-400">How BUBE works</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
